import React, { useState, useEffect, useCallback } from 'react';
import styles from './CategoriesList.module.css';
import { GET, PUT, POST } from '../../../../config/api';

const emptyForm = {
  name: '',
  thumbnail: '',
  disabled: false,
  commingSoon: false,
  images: '',
};

const CategoriesList = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, message } = await GET('website/admin/categories');
    if (data?.categories) {
      setCategories(data.categories);
    } else {
      setError(message || 'Failed to load categories');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const buildPayload = (formData) => ({
    name: formData.name.trim(),
    thumbnail: formData.thumbnail.trim() || undefined,
    disabled: formData.disabled,
    commingSoon: formData.commingSoon,
    images: formData.images
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean),
  });

  const startAdd = () => {
    setShowAddForm(true);
    setEditingId(null);
    setAddForm(emptyForm);
    setSaveMessage(null);
  };

  const cancelAdd = () => {
    setShowAddForm(false);
    setAddForm(emptyForm);
    setSaveMessage(null);
  };

  const handleCreate = async () => {
    setSaving(true);
    setSaveMessage(null);

    const { data, message } = await POST('website/admin/categories', {}, buildPayload(addForm));

    if (data?.category) {
      setCategories((prev) => [...prev, data.category].sort((a, b) => a.name.localeCompare(b.name)));
      setSaveMessage({ type: 'success', text: 'Category created.' });
      setShowAddForm(false);
      setAddForm(emptyForm);
    } else {
      setSaveMessage({ type: 'error', text: message || 'Failed to create category.' });
    }

    setSaving(false);
  };

  const startEdit = (category) => {
    setShowAddForm(false);
    setEditingId(category._id);
    setForm({
      name: category.name || '',
      thumbnail: category.thumbnail || '',
      disabled: !!category.disabled,
      commingSoon: !!category.commingSoon,
      images: (category.images || []).join('\n'),
    });
    setSaveMessage(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setSaveMessage(null);
  };

  const handleSave = async (categoryId) => {
    setSaving(true);
    setSaveMessage(null);

    const { data, message } = await PUT(`website/admin/categories/${categoryId}`, {}, buildPayload(form));

    if (data?.category) {
      setCategories((prev) =>
        prev.map((cat) => (cat._id === categoryId ? data.category : cat)),
      );
      setSaveMessage({ type: 'success', text: 'Category updated.' });
      setEditingId(null);
      setForm(emptyForm);
    } else {
      setSaveMessage({ type: 'error', text: message || 'Failed to update category.' });
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <div className={styles['spinner-text']}>Loading categories...</div>
      </div>
    );
  }

  if (error) {
    return <div className={styles.error}>Error: {error}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.hero}>
        <div className={styles.brand}>
          <img src="/logo192.png" alt="Tellmiz logo" className={styles['brand-logo']} />
          <div>
            <p className={styles.eyebrow}>Tellmiz Admin</p>
            <h1>Categories</h1>
            <p className={styles.subhead}>View, add, and edit app categories.</p>
          </div>
        </div>
        <div className={styles['stat-row']}>
          <div className={styles['stat-card']}>
            <span>Total</span>
            <strong>{categories.length}</strong>
          </div>
          <div className={styles['stat-card']}>
            <span>Active</span>
            <strong>{categories.filter((c) => !c.disabled).length}</strong>
          </div>
          <div className={styles['stat-card']}>
            <span>Disabled</span>
            <strong>{categories.filter((c) => c.disabled).length}</strong>
          </div>
          <div className={styles['stat-card']}>
            <span>Coming Soon</span>
            <strong>{categories.filter((c) => c.commingSoon).length}</strong>
          </div>
        </div>
      </div>

      {saveMessage && (
        <div className={`${styles.banner} ${styles[saveMessage.type]}`}>
          {saveMessage.text}
        </div>
      )}

      <div className={styles.toolbar}>
        <button className={styles['add-btn']} onClick={startAdd} disabled={showAddForm || !!editingId}>
          Add category
        </button>
        <button className={styles['refresh-btn']} onClick={fetchCategories} disabled={loading}>
          Refresh
        </button>
      </div>

      {showAddForm && (
        <div className={styles['add-panel']}>
          <h2 className={styles['add-panel-title']}>New category</h2>
          <div className={styles['edit-form']}>
            <label>
              Name <span className={styles.required}>*</span>
              <input
                type="text"
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                placeholder="e.g. Schools"
              />
            </label>
            <label>
              Thumbnail URL
              <input
                type="text"
                value={addForm.thumbnail}
                onChange={(e) => setAddForm({ ...addForm, thumbnail: e.target.value })}
                placeholder="https://..."
              />
            </label>
            <label>
              Images (one URL per line)
              <textarea
                rows={3}
                value={addForm.images}
                onChange={(e) => setAddForm({ ...addForm, images: e.target.value })}
              />
            </label>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={addForm.disabled}
                onChange={(e) => setAddForm({ ...addForm, disabled: e.target.checked })}
              />
              Disabled
            </label>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={addForm.commingSoon}
                onChange={(e) => setAddForm({ ...addForm, commingSoon: e.target.checked })}
              />
              Coming Soon
            </label>
            <div className={styles['form-actions']}>
              <button
                className={styles['save-btn']}
                onClick={handleCreate}
                disabled={saving || !addForm.name.trim()}
              >
                {saving ? 'Creating...' : 'Create'}
              </button>
              <button className={styles['cancel-btn']} onClick={cancelAdd} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles['categories-grid']}>
        {categories.map((category) => {
          const isEditing = editingId === category._id;

          return (
            <div key={category._id} className={styles['category-card']}>
              <div className={styles['category-header']}>
                <div className={styles['category-thumb']}>
                  {category.thumbnail ? (
                    <img src={category.thumbnail} alt={category.name} />
                  ) : (
                    <div className={styles['thumb-placeholder']}>No image</div>
                  )}
                </div>
                <div>
                  <h2>{category.name}</h2>
                  <div className={styles['pill-row']}>
                    <span className={`${styles.pill} ${category.disabled ? styles['pill-warn'] : styles['pill-ok']}`}>
                      {category.disabled ? 'Disabled' : 'Active'}
                    </span>
                    {category.commingSoon && (
                      <span className={`${styles.pill} ${styles['pill-soon']}`}>Coming Soon</span>
                    )}
                  </div>
                </div>
              </div>

              {isEditing ? (
                <div className={styles['edit-form']}>
                  <label>
                    Name
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </label>
                  <label>
                    Thumbnail URL
                    <input
                      type="text"
                      value={form.thumbnail}
                      onChange={(e) => setForm({ ...form, thumbnail: e.target.value })}
                    />
                  </label>
                  <label>
                    Images (one URL per line)
                    <textarea
                      rows={3}
                      value={form.images}
                      onChange={(e) => setForm({ ...form, images: e.target.value })}
                    />
                  </label>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={form.disabled}
                      onChange={(e) => setForm({ ...form, disabled: e.target.checked })}
                    />
                    Disabled
                  </label>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={form.commingSoon}
                      onChange={(e) => setForm({ ...form, commingSoon: e.target.checked })}
                    />
                    Coming Soon
                  </label>
                  <div className={styles['form-actions']}>
                    <button
                      className={styles['save-btn']}
                      onClick={() => handleSave(category._id)}
                      disabled={saving || !form.name.trim()}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button className={styles['cancel-btn']} onClick={cancelEdit} disabled={saving}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles['category-meta']}>
                  <span><strong>ID:</strong> {category._id}</span>
                  <span><strong>Images:</strong> {category.images?.length ?? 0}</span>
                  <span><strong>Updated:</strong> {category.updatedAt ? new Date(category.updatedAt).toLocaleString() : '—'}</span>
                  <button className={styles['edit-btn']} onClick={() => startEdit(category)}>
                    Edit
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategoriesList;
